import { createAsyncThunk } from '@reduxjs/toolkit'

import { type CardIdType } from '@/defs/card'
import { parseCardId } from '@/utils/cardUtil'
import { type SignInProps, type SignUpProps } from '@/utils/firebaseAuthUtil'
import { type setProfile } from '@/utils/firebaseStoreUtil'

import { userActions } from './slice'

const deleteUser = createAsyncThunk('user/deleteUser', async (_, { dispatch }) => {
  const firebaseAuthUtil = await import('@/utils/firebaseAuthUtil')

  await firebaseAuthUtil.removeFirebaseUser()

  await dispatch(signOut())
})

const signIn = createAsyncThunk('user/signInThunk', async (props: SignInProps, { dispatch }) => {
  dispatch(userActions.authLoading())

  const [firebaseAuthUtil, firebaseStoreUtil] = await Promise.all([
    import('@/utils/firebaseAuthUtil'),
    import('@/utils/firebaseStoreUtil'),
  ])

  const uid = await firebaseAuthUtil.signIn(props)
  const profile = await firebaseStoreUtil.fetchProfile({ uid })

  dispatch(userActions.authSignIn(profile))
})

const signOut = createAsyncThunk('user/signOutThunk', async (_, { dispatch }) => {
  const firebaseAuthUtil = await import('@/utils/firebaseAuthUtil')
  await firebaseAuthUtil.signOut()

  dispatch(userActions.authSignOut())
})

const signUp = createAsyncThunk('user/signUpThunk', async (props: SignUpProps, { dispatch }) => {
  const firebaseAuthUtil = await import('@/utils/firebaseAuthUtil')

  await firebaseAuthUtil.signUp(props)

  await dispatch(signIn({ email: props.email, password: props.password })).unwrap()
})

const update = createAsyncThunk('user/updateThunk', async (props: Parameters<typeof setProfile>[0], { dispatch }) => {
  const [firebaseAuthUtil, firebaseStoreUtil] = await Promise.all([
    import('@/utils/firebaseAuthUtil'),
    import('@/utils/firebaseStoreUtil'),
  ])

  const uid = firebaseAuthUtil.authFirebase.currentUser?.uid
  if (!uid) throw new Error('You must be signed in to complete this action')
  await firebaseStoreUtil.setProfile(props)
  const profile = await firebaseStoreUtil.fetchProfile({ uid })

  dispatch(userActions.update(profile))
})

const setAlternateArt = createAsyncThunk(
  'user/setAlternateArt',
  async ({ cardId, scryfallId }: { cardId: CardIdType; scryfallId: string }, { dispatch }) => {
    const firebaseStoreUtil = await import('@/utils/firebaseStoreUtil')
    const { slug } = parseCardId(cardId)
    const alternateArt = { [slug]: scryfallId || firebaseStoreUtil.deleteField() }
    await dispatch(update({ profile: { alternateArt } })).unwrap()
  },
)

export const userThunks = { deleteUser, setAlternateArt, signIn, signOut, signUp, update }
